import React, { useState, useMemo } from 'react'
import './ContributionHeatmap.scss'

interface ViewData {
  views: Record<string, number>
}

interface HoveredCell {
  date: Date
  count: number
}

interface Activity {
  id?: string
  jobId?: string
  jobTitle?: string
  company?: string
  state: string
  timestamp: string | number
}

interface ContributionHeatmapProps {
  activities?: Activity[]
}

function ContributionHeatmap({ activities = [] }: ContributionHeatmapProps) {
  const [hoveredCell, setHoveredCell] = useState<HoveredCell | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })

  // Count activities by date
  const viewData = useMemo<ViewData | null>(() => {
    if (!activities || activities.length === 0) {
      return null
    }

    const views: Record<string, number> = {}
    
    activities.forEach(activity => {
      if (!activity.timestamp) return
      
      // Parse the timestamp (could be ISO string or Unix timestamp)
      let date: Date
      if (typeof activity.timestamp === 'string') {
        date = new Date(activity.timestamp)
      } else if (typeof activity.timestamp === 'number') {
        // If it's a Unix timestamp, check if it's in seconds or milliseconds
        date = activity.timestamp < 10000000000 
          ? new Date(activity.timestamp * 1000) // seconds
          : new Date(activity.timestamp) // milliseconds
      } else {
        return
      }
      
      // Skip invalid dates
      if (isNaN(date.getTime())) return
      
      // Format as YYYY-MM-DD
      const dateStr = date.toISOString().split('T')[0]
      
      // Count activities per date
      views[dateStr] = (views[dateStr] || 0) + 1
    })
    
    return { views }
  }, [activities])

  // Generate array of dates for the last year (or 53 weeks to match GitHub)
  const generateDateArray = (): Date[] => {
    const dates: Date[] = []
    const today = new Date()
    const startDate = new Date(today)
    startDate.setDate(today.getDate() - 364) // 52 weeks * 7 days - 1 = 363 days, but we want 364 to get 365 days total

    for (let i = 0; i < 365; i++) {
      const date = new Date(startDate)
      date.setDate(startDate.getDate() + i)
      dates.push(date)
    }
    return dates
  }

  // Get view count for a specific date
  const getViewCount = (date: Date): number => {
    if (!viewData || !viewData.views) return 0
    
    const dateStr = date.toISOString().split('T')[0] // YYYY-MM-DD format
    return viewData.views[dateStr] || 0
  }

  // Get color intensity based on view count
  const getColorIntensity = (count: number): string => {
    if (count === 0) return 'level-0'
    if (count <= 2) return 'level-1'
    if (count <= 4) return 'level-2'
    if (count <= 6) return 'level-3'
    return 'level-4'
  }

  // Format date for display
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }

  // Group dates by week for display (Sunday to Saturday)
  const groupByWeek = (dates: Date[]): (Date | null)[][] => {
    if (dates.length === 0) return []
    
    const weeks: (Date | null)[][] = []
    let currentWeek: (Date | null)[] = []
    
    // Pad the first week if it doesn't start on Sunday
    const firstDate = dates[0]
    const firstDayOfWeek = firstDate.getDay()
    for (let i = 0; i < firstDayOfWeek; i++) {
      currentWeek.push(null)
    }
    
    dates.forEach((date, index) => {
      const dayOfWeek = date.getDay()
      
      // If it's Sunday and we have a previous week, save it
      if (dayOfWeek === 0 && currentWeek.length > 0) {
        weeks.push(currentWeek)
        currentWeek = []
      }
      
      currentWeek.push(date)
      
      // If it's the last date, finalize the week
      if (index === dates.length - 1) {
        // Pad the end of the last week if needed
        while (currentWeek.length < 7) {
          currentWeek.push(null)
        }
        weeks.push(currentWeek)
      }
    })
    
    return weeks
  }

  const dates = generateDateArray()
  const weeks = groupByWeek(dates)
  const totalViews = viewData?.views ? Object.values(viewData.views).reduce((sum, count) => sum + count, 0) : 0

  // Show message if no data
  if (!viewData || !viewData.views || Object.keys(viewData.views).length === 0) {
    return (
      <div className="contribution-heatmap-container">
        <h3 className="h5 fw-bold mb-3" style={{
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
        }}>
          Interview Prep Activity
        </h3>
        <div className="text-center py-5">
          <p className="text-muted mb-0" style={{
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
          }}>
            No activities yet. Start using the app to see your activity heatmap!
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="contribution-heatmap-container">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <h3 className="h5 fw-bold mb-0" style={{
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
        }}>
          Interview Prep Activity
        </h3>
        <div className="d-flex flex-column align-items-end gap-2">
          <div className="text-muted small" style={{
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
          }}>
            {totalViews} {totalViews === 1 ? 'activity' : 'activities'} in the last year
          </div>
          <div className="heatmap-legend d-flex align-items-center gap-2">
            <span className="small text-muted" style={{
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
            }}>
              Less
            </span>
            <div className="heatmap-legend-cells">
              <div className="heatmap-cell level-0" />
              <div className="heatmap-cell level-1" />
              <div className="heatmap-cell level-2" />
              <div className="heatmap-cell level-3" />
              <div className="heatmap-cell level-4" />
            </div>
            <span className="small text-muted" style={{
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
            }}>
              More
            </span>
          </div>
        </div>
      </div>

      <div className="heatmap-wrapper">
        <div className="heatmap-inner">
          <div className="heatmap-months mb-2">
            {/* Calculate month positions based on actual dates */}
            {(() => {
              // Find the first day of each month in the dates array
              const monthFirstDay = new Map<number, Date>()
              const monthWeekCounts = new Map<number, number>()
              
              dates.forEach((date) => {
                const month = date.getMonth()
                const day = date.getDate()
                
                // Track if this month has dates
                monthWeekCounts.set(month, (monthWeekCounts.get(month) || 0) + 1)
                
                // Find the first day (day 1) of each month
                if (day === 1 && !monthFirstDay.has(month)) {
                  monthFirstDay.set(month, date)
                }
              })
              
              // Find which week column each first day falls into
              const monthToWeekIdx = new Map<number, number>()
              monthFirstDay.forEach((firstDay, month) => {
                // Find which week contains this date
                weeks.forEach((week, weekIdx) => {
                  const hasDate = week.some(d => 
                    d !== null && 
                    d.getMonth() === firstDay.getMonth() && 
                    d.getDate() === firstDay.getDate() &&
                    d.getFullYear() === firstDay.getFullYear()
                  )
                  if (hasDate && !monthToWeekIdx.has(month)) {
                    monthToWeekIdx.set(month, weekIdx)
                  }
                })
              })
              
              // Track last week for months that appear at both ends
              const monthLastWeek = new Map<number, number>()
              weeks.forEach((week, weekIdx) => {
                const firstDate = week.find(date => date !== null)
                if (firstDate) {
                  const month = firstDate.getMonth()
                  monthLastWeek.set(month, weekIdx)
                }
              })
              
              // Only show labels for months that span more than 2 weeks
              const monthLabels: Array<{ name: string; weekIdx: number }> = []
              monthWeekCounts.forEach((weekCount, month) => {
                if (weekCount > 2) {
                  const firstWeekIdx = monthToWeekIdx.get(month)
                  const lastWeekIdx = monthLastWeek.get(month)
                  
                  if (firstWeekIdx !== undefined) {
                    const monthName = new Date(2024, month, 1).toLocaleDateString('en-US', { month: 'short' })
                    
                    // If month appears at both ends of the year (first is early, last is late), 
                    // show at the last occurrence. Otherwise show at the first day of the month.
                    const appearsAtBothEnds = firstWeekIdx < weeks.length * 0.2 && 
                                             lastWeekIdx !== undefined && 
                                             lastWeekIdx > weeks.length * 0.8
                    const weekIdxToUse = appearsAtBothEnds && lastWeekIdx !== undefined ? lastWeekIdx : firstWeekIdx
                    
                    monthLabels.push({ name: monthName, weekIdx: weekIdxToUse })
                  }
                }
              })
              
              // Calculate position in pixels: each week is 11px wide + 3px gap
              // Position is calculated as: weekIdx * (11px + 3px) = weekIdx * 14px
              return monthLabels.map((month, idx) => {
                const position = month.weekIdx * 14 // 11px cell + 3px gap
                return (
                  <span 
                    key={`${month.name}-${idx}`} 
                    className="heatmap-month-label" 
                    style={{ 
                      left: `${position}px` 
                    }}
                  >
                    {month.name}
                  </span>
                )
              })
            })()}
          </div>

          <div className="heatmap-content">
            <div className="heatmap-weekdays">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                <div key={day} className="heatmap-weekday-label">
                  {idx % 2 === 0 ? day : ''}
                </div>
              ))}
            </div>

            <div className="heatmap-grid">
            {weeks.map((week, weekIdx) => (
              <div key={weekIdx} className="heatmap-week">
                {week.map((date, dayIdx) => {
                  if (!date) {
                    return (
                      <div
                        key={`${weekIdx}-${dayIdx}`}
                        className="heatmap-cell level-0"
                        style={{ opacity: 0 }}
                      />
                    )
                  }
                  
                  const count = getViewCount(date)
                  const intensity = getColorIntensity(count)
                  const dateStr = date.toISOString().split('T')[0]
                  
                  return (
                    <div
                      key={`${weekIdx}-${dayIdx}`}
                      className={`heatmap-cell ${intensity}`}
                      data-date={dateStr}
                      data-count={count}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect()
                        setTooltipPosition({
                          x: rect.left + rect.width / 2,
                          y: rect.top
                        })
                        setHoveredCell({ date, count })
                      }}
                      onMouseLeave={() => setHoveredCell(null)}
                      title={`${formatDate(date)}: ${count} ${count === 1 ? 'activity' : 'activities'}`}
                    />
                  )
                })}
              </div>
            ))}
          </div>
          </div>
        </div>

        {hoveredCell && (
          <div 
            className="heatmap-tooltip"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`
            }}
          >
            <strong>{hoveredCell.count} {hoveredCell.count === 1 ? 'activity' : 'activities'}</strong>
            <br />
            <span style={{ opacity: 0.8 }}>{formatDate(hoveredCell.date)}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default ContributionHeatmap

